import { useEffect, useState } from "react";
import { pageGet } from "@/lib/pageGet";
import { coinsForDollars } from "@/lib/coins";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const money = (n) => {
  if (n == null) return "";
  return Number.isInteger(Number(n)) ? `$${Number(n)}` : `$${Number(n).toFixed(2)}`;
};

let _cache = null; // module-level cache so every CTA shares one fetch
let _promise = null;

const load = () => {
  if (!_promise) {
    _promise = Promise.all([
      pageGet(`${API}/settings/pricing`),
      pageGet(`${API}/subscribe/early-bird`),
      pageGet(`${API}/subscribe/promo`),
    ]).then(([pricing, eb, promo]) => {
      const plans = pricing?.plans || {};
      const regular = plans.monthly?.price;
      const earlyBirdActive = !!(eb?.active && eb?.eligible);
      // Same precedence as checkout: while Early Bird has spots, no promo applies.
      const promos = eb?.active ? {} : (promo?.promos || {});
      const promoMonthly = promos.monthly?.price;
      const join = earlyBirdActive ? eb?.prices?.monthly : (promoMonthly ?? regular);
      _cache = {
        loading: join == null,
        pricing, earlyBird: eb, promos,
        earlyBirdActive,
        promoActive: !earlyBirdActive && promoMonthly != null,
        remaining: eb?.remaining ?? null,
        cap: eb?.cap ?? 100,
        regularMonthly: regular,
        regularMonthlyLabel: money(regular),
        bossMonthlyLabel: money(regular),
        bossAnnualLabel: money(plans.annual?.price),
        donMonthly: plans.premium_monthly?.price,
        donMonthlyLabel: money(plans.premium_monthly?.price),
        donAnnualLabel: money(plans.premium_annual?.price),
        joinPrice: money(join),
        joinPriceMonthly: money(join),
        joinCoins: join == null ? null : coinsForDollars(join),
        joinDollars: money(join),
      };
      return _cache;
    }).finally(() => { _promise = null; }); // dedupe concurrent mounts; later mounts refetch
  }
  return _promise;
};

/**
 * Single price source for EVERY public join CTA / price mention: the live Early Bird
 * Boss monthly price while the viewer is eligible, else a live Boss monthly promo,
 * else the regular price. Nothing is hardcoded; while loading, prices are empty.
 * Use `resolveCopy(str)` to fill {{price}} in CMS copy.
 */
export const useJoinPrice = ({ skip = false } = {}) => {
  const [state, setState] = useState(_cache || { loading: true });

  useEffect(() => {
    if (skip) return undefined; // e.g. pre-launch landing, where these APIs are gated
    let alive = true;
    load().then((next) => { if (alive) setState(next); });
    return () => { alive = false; };
  }, [skip]);

  const resolveCopy = (str) => (typeof str === "string" ? str.replace(/\{\{\s*price\s*\}\}/g, state.joinPrice || "") : str);
  return { ...state, resolveCopy };
};

export default useJoinPrice;
